import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Modal, Button, Slider, Icon, Popover } from 'antd'
import { transitions } from '../../helper/data.video'
import TransitionDisplay from '../Transition/TransitionDisplay'
import './SceneTransition.scss'

// ─── CANVAS TRANSITION ENGINE ─────────────────────────────────────────────────

function drawA(ctx, w, h) {
  ctx.fillStyle = '#e63946'
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = '#fff'
  ctx.font = `bold ${(w * 0.36) | 0}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('A', w / 2, h / 2)
}

function drawB(ctx, w, h) {
  ctx.fillStyle = '#457b9d'
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = '#fff'
  ctx.font = `bold ${(w * 0.36) | 0}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('B', w / 2, h / 2)
}

// Seeded random for dissolve consistency
function seededRand(seed) {
  let s = seed
  return () => {
    s = (s * 16807) % 2147483647
    return (s - 1) / 2147483646
  }
}
const _dr = seededRand(42)
const dissolveOrder = Array.from({ length: 32 * 32 }, (_, i) => ({
  i,
  r: _dr()
}))
  .sort((a, b) => a.r - b.r)
  .map(x => x.i)

function triWipe(c, w, h, p, cx, cy, ax, ay, bx, by) {
  drawA(c, w, h)
  c.save()
  c.beginPath()
  const d = p * 2
  c.moveTo(cx, cy)
  if (d <= 1) {
    c.lineTo(cx + (ax - cx) * d, cy + (ay - cy) * d)
  } else {
    c.lineTo(ax, ay)
    c.lineTo(bx * (d - 1) + ax * (2 - d), by * (d - 1) + ay * (2 - d))
  }
  c.closePath()
  c.clip()
  drawB(c, w, h)
  c.restore()
}

function smoothH(c, w, h, p, leftToRight) {
  const N = 32
  drawA(c, w, h)
  for (let i = 0; i < N; i++) {
    const y = (i / N) * h,
      rh = h / N
    const wave = Math.sin((i / N) * Math.PI) * 0.18
    const t = leftToRight
      ? Math.min(Math.max((p - wave) / 0.64, 0), 1)
      : Math.min(Math.max((p - (0.36 - wave)) / 0.64, 0), 1)
    const edge = leftToRight ? (1 - t) * w : t * w
    c.save()
    c.beginPath()
    if (leftToRight) c.rect(edge, y, w - edge, rh)
    else c.rect(0, y, edge, rh)
    c.clip()
    drawB(c, w, h)
    c.restore()
  }
}

function smoothV(c, w, h, p, upward) {
  const N = 32
  drawA(c, w, h)
  for (let i = 0; i < N; i++) {
    const x = (i / N) * w,
      cw = w / N
    const wave = Math.sin((i / N) * Math.PI) * 0.18
    const t = upward
      ? Math.min(Math.max((p - wave) / 0.64, 0), 1)
      : Math.min(Math.max((p - (0.36 - wave)) / 0.64, 0), 1)
    const edge = upward ? (1 - t) * h : t * h
    c.save()
    c.beginPath()
    if (upward) c.rect(x, 0, cw, edge)
    else c.rect(x, edge, cw, h - edge)
    c.clip()
    drawB(c, w, h)
    c.restore()
  }
}

function sliceH(c, w, h, p, left) {
  const N = 8
  drawA(c, w, h)
  for (let i = 0; i < N; i++) {
    const y = (i / N) * h,
      rh = h / N
    const delay = ((left ? i : N - 1 - i) / N) * 0.5
    const t = Math.min(Math.max((p - delay) / 0.5, 0), 1)
    const off = left ? -w * (1 - t) : w * (1 - t)
    c.save()
    c.beginPath()
    c.rect(0, y, w, rh)
    c.clip()
    c.save()
    c.translate(off, 0)
    drawB(c, w, h)
    c.restore()
    c.restore()
  }
}

function sliceV(c, w, h, p, up) {
  const N = 8
  drawA(c, w, h)
  for (let i = 0; i < N; i++) {
    const x = (i / N) * w,
      cw = w / N
    const delay = ((up ? i : N - 1 - i) / N) * 0.5
    const t = Math.min(Math.max((p - delay) / 0.5, 0), 1)
    const off = up ? -h * (1 - t) : h * (1 - t)
    c.save()
    c.beginPath()
    c.rect(x, 0, cw, h)
    c.clip()
    c.save()
    c.translate(0, off)
    drawB(c, w, h)
    c.restore()
    c.restore()
  }
}

function windH(c, w, h, p, left) {
  const N = 10
  drawA(c, w, h)
  for (let i = 0; i < N; i++) {
    const y = (i / N) * h,
      rh = h / N
    const delay = (i % 2) * 0.12
    const t = Math.min(Math.max((p - delay) / 0.88, 0), 1)
    const stretch = 1 + (i % 3) * 0.25
    const off = left ? -w * (1 - t) * stretch : w * (1 - t) * stretch
    c.save()
    c.beginPath()
    c.rect(0, y, w, rh)
    c.clip()
    c.save()
    c.translate(off, 0)
    drawB(c, w, h)
    c.restore()
    c.restore()
  }
}

function windV(c, w, h, p, up) {
  const N = 10
  drawA(c, w, h)
  for (let i = 0; i < N; i++) {
    const x = (i / N) * w,
      cw = w / N
    const delay = (i % 2) * 0.12
    const t = Math.min(Math.max((p - delay) / 0.88, 0), 1)
    const stretch = 1 + (i % 3) * 0.25
    const off = up ? -h * (1 - t) * stretch : h * (1 - t) * stretch
    c.save()
    c.beginPath()
    c.rect(x, 0, cw, h)
    c.clip()
    c.save()
    c.translate(0, off)
    drawB(c, w, h)
    c.restore()
    c.restore()
  }
}

function getRenderer(name) {
  switch (name) {
    case 'fade':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.globalAlpha = p
        drawB(c, w, h)
        c.globalAlpha = 1
      }
    case 'fadein':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.globalAlpha = Math.min(p * 2, 1)
        drawB(c, w, h)
        c.globalAlpha = 1
      }
    case 'fadeout':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.globalAlpha = Math.max(p * 2 - 1, 0)
        drawB(c, w, h)
        c.globalAlpha = 1
      }
    case 'fadeblack':
      return (c, w, h, p) => {
        if (p < 0.5) {
          drawA(c, w, h)
          c.globalAlpha = p * 2
          c.fillStyle = '#000'
          c.fillRect(0, 0, w, h)
        } else {
          drawB(c, w, h)
          c.globalAlpha = 2 - p * 2
          c.fillStyle = '#000'
          c.fillRect(0, 0, w, h)
        }
        c.globalAlpha = 1
      }
    case 'fadewhite':
      return (c, w, h, p) => {
        if (p < 0.5) {
          drawA(c, w, h)
          c.globalAlpha = p * 2
          c.fillStyle = '#fff'
          c.fillRect(0, 0, w, h)
        } else {
          drawB(c, w, h)
          c.globalAlpha = 2 - p * 2
          c.fillStyle = '#fff'
          c.fillRect(0, 0, w, h)
        }
        c.globalAlpha = 1
      }
    case 'fadegrays':
      return (c, w, h, p) => {
        if (p < 0.5) {
          drawA(c, w, h)
          c.globalAlpha = p * 2
          c.fillStyle = '#888'
          c.fillRect(0, 0, w, h)
        } else {
          drawB(c, w, h)
          c.globalAlpha = 2 - p * 2
          c.fillStyle = '#888'
          c.fillRect(0, 0, w, h)
        }
        c.globalAlpha = 1
      }
    case 'dissolve':
      return (c, w, h, p) => {
        const cols = 32,
          rows = 32,
          cw = w / cols,
          ch = h / rows,
          n = dissolveOrder.length
        drawA(c, w, h)
        const threshold = p * n
        for (let i = 0; i < threshold && i < n; i++) {
          const idx = dissolveOrder[i],
            col = idx % cols,
            row = (idx / cols) | 0
          c.save()
          c.beginPath()
          c.rect(col * cw, row * ch, cw, ch)
          c.clip()
          drawB(c, w, h)
          c.restore()
        }
      }

    case 'wipeleft':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(w * (1 - p), 0, w * p, h)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'wiperight':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, 0, w * p, h)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'wipeup':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, h * (1 - p), w, h * p)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'wipedown':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, 0, w, h * p)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }

    case 'slideleft':
      return (c, w, h, p) => {
        c.save()
        c.translate(-w * p, 0)
        drawA(c, w, h)
        c.restore()
        c.save()
        c.translate(w * (1 - p), 0)
        drawB(c, w, h)
        c.restore()
      }
    case 'slideright':
      return (c, w, h, p) => {
        c.save()
        c.translate(w * p, 0)
        drawA(c, w, h)
        c.restore()
        c.save()
        c.translate(-w * (1 - p), 0)
        drawB(c, w, h)
        c.restore()
      }
    case 'slideup':
      return (c, w, h, p) => {
        c.save()
        c.translate(0, -h * p)
        drawA(c, w, h)
        c.restore()
        c.save()
        c.translate(0, h * (1 - p))
        drawB(c, w, h)
        c.restore()
      }
    case 'slidedown':
      return (c, w, h, p) => {
        c.save()
        c.translate(0, h * p)
        drawA(c, w, h)
        c.restore()
        c.save()
        c.translate(0, -h * (1 - p))
        drawB(c, w, h)
        c.restore()
      }

    case 'smoothleft':
      return (c, w, h, p) => smoothH(c, w, h, p, true)
    case 'smoothright':
      return (c, w, h, p) => smoothH(c, w, h, p, false)
    case 'smoothup':
      return (c, w, h, p) => smoothV(c, w, h, p, true)
    case 'smoothdown':
      return (c, w, h, p) => smoothV(c, w, h, p, false)

    case 'circlecrop':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.arc(
          w / 2,
          h / 2,
          Math.max(w, h) * Math.abs(p - 0.5) * 2,
          0,
          Math.PI * 2
        )
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'circleopen':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.arc(w / 2, h / 2, Math.max(w, h) * p, 0, Math.PI * 2)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'circleclose':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.arc(w / 2, h / 2, Math.max(w, h) * (1 - p), 0, Math.PI * 2)
        c.clip()
        drawA(c, w, h)
        c.restore()
      }

    case 'coverleft':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, 0, w * p, h)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'coverright':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(w * (1 - p), 0, w * p, h)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'coverup':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, h * (1 - p), w, h * p)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'coverdown':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, 0, w, h * p)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }

    case 'revealleft':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.rect(w * p, 0, w * (1 - p), h)
        c.clip()
        drawA(c, w, h)
        c.restore()
      }
    case 'revealright':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, 0, w * (1 - p), h)
        c.clip()
        drawA(c, w, h)
        c.restore()
      }
    case 'revealup':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, 0, w, h * (1 - p))
        c.clip()
        drawA(c, w, h)
        c.restore()
      }
    case 'revealdown':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, h * p, w, h * (1 - p))
        c.clip()
        drawA(c, w, h)
        c.restore()
      }

    case 'horzopen':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, h / 2 - (h * p) / 2, w, h * p)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'horzclose':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.rect(0, h / 2 - (h * (1 - p)) / 2, w, h * (1 - p))
        c.clip()
        drawA(c, w, h)
        c.restore()
      }
    case 'vertopen':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.rect(w / 2 - (w * p) / 2, 0, w * p, h)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }
    case 'vertclose':
      return (c, w, h, p) => {
        drawB(c, w, h)
        c.save()
        c.beginPath()
        c.rect(w / 2 - (w * (1 - p)) / 2, 0, w * (1 - p), h)
        c.clip()
        drawA(c, w, h)
        c.restore()
      }
    case 'rectcrop':
      return (c, w, h, p) => {
        drawA(c, w, h)
        const s = Math.abs(p - 0.5) * 2
        c.save()
        c.beginPath()
        c.rect(w / 2 - (w * s) / 2, h / 2 - (h * s) / 2, w * s, h * s)
        c.clip()
        drawB(c, w, h)
        c.restore()
      }

    case 'diagtl':
      return (c, w, h, p) => triWipe(c, w, h, p, 0, 0, w, 0, 0, h)
    case 'diagtr':
      return (c, w, h, p) => triWipe(c, w, h, p, w, 0, 0, 0, w, h)
    case 'diagbl':
      return (c, w, h, p) => triWipe(c, w, h, p, 0, h, 0, 0, w, h)
    case 'diagbr':
      return (c, w, h, p) => triWipe(c, w, h, p, w, h, w, 0, 0, h)
    case 'radial':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.save()
        c.beginPath()
        c.moveTo(w / 2, h / 2)
        c.arc(
          w / 2,
          h / 2,
          Math.max(w, h),
          -Math.PI / 2,
          -Math.PI / 2 + p * Math.PI * 2
        )
        c.closePath()
        c.clip()
        drawB(c, w, h)
        c.restore()
      }

    case 'hlslice':
      return (c, w, h, p) => sliceH(c, w, h, p, true)
    case 'hrslice':
      return (c, w, h, p) => sliceH(c, w, h, p, false)
    case 'vuslice':
      return (c, w, h, p) => sliceV(c, w, h, p, true)
    case 'vdslice':
      return (c, w, h, p) => sliceV(c, w, h, p, false)

    case 'pixelize':
      return (c, w, h, p) => {
        if (p < 0.5) {
          const bs = Math.max(2, ((1 - p * 2) * w * 0.25) | 0)
          drawA(c, w, h)
          for (let r = 0; r < h; r += bs)
            for (let col = 0; col < w; col += bs) {
              c.fillStyle =
                ((r / bs + col / bs) | 0) % 2 === 0 ? '#d62828' : '#c1121f'
              c.fillRect(col, r, bs, bs)
            }
        } else {
          const bs = Math.max(2, ((p * 2 - 1) * w * 0.25) | 0)
          drawB(c, w, h)
          if (bs > 3)
            for (let r = 0; r < h; r += bs)
              for (let col = 0; col < w; col += bs) {
                c.fillStyle =
                  ((r / bs + col / bs) | 0) % 2 === 0 ? '#3a7ca5' : '#2c6fad'
                c.fillRect(col, r, bs, bs)
              }
        }
      }
    case 'squeezeh':
      return (c, w, h, p) => {
        if (p < 0.5) {
          const s = 1 - p * 2
          c.save()
          c.translate(w / 2, 0)
          c.scale(s, 1)
          c.translate(-w / 2, 0)
          drawA(c, w, h)
          c.restore()
        } else {
          const s = (p - 0.5) * 2
          c.save()
          c.translate(w / 2, 0)
          c.scale(s, 1)
          c.translate(-w / 2, 0)
          drawB(c, w, h)
          c.restore()
        }
      }
    case 'squeezev':
      return (c, w, h, p) => {
        if (p < 0.5) {
          const s = 1 - p * 2
          c.save()
          c.translate(0, h / 2)
          c.scale(1, s)
          c.translate(0, -h / 2)
          drawA(c, w, h)
          c.restore()
        } else {
          const s = (p - 0.5) * 2
          c.save()
          c.translate(0, h / 2)
          c.scale(1, s)
          c.translate(0, -h / 2)
          drawB(c, w, h)
          c.restore()
        }
      }
    case 'zoomin':
      return (c, w, h, p) => {
        drawA(c, w, h)
        const s = 1 + (1 - p) * 2
        c.save()
        c.translate(w / 2, h / 2)
        c.scale(s, s)
        c.translate(-w / 2, -h / 2)
        c.globalAlpha = p
        drawB(c, w, h)
        c.globalAlpha = 1
        c.restore()
      }
    case 'hblur':
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.globalAlpha = p
        drawB(c, w, h)
        c.globalAlpha = 1
      }

    // Wind — not in your categories but kept for completeness if added later
    case 'hlwind':
      return (c, w, h, p) => windH(c, w, h, p, true)
    case 'hrwind':
      return (c, w, h, p) => windH(c, w, h, p, false)
    case 'vuwind':
      return (c, w, h, p) => windV(c, w, h, p, true)
    case 'vdwind':
      return (c, w, h, p) => windV(c, w, h, p, false)

    default:
      return (c, w, h, p) => {
        drawA(c, w, h)
        c.globalAlpha = p
        drawB(c, w, h)
        c.globalAlpha = 1
      }
  }
}

// ─── REUSABLE CANVAS PREVIEW COMPONENT ───────────────────────────────────────
// Props: transitionId (string), width, height, loopMs, pauseMs
// When paused=true the animation stops; when it becomes false it restarts.

const TransitionCanvas = ({
  transitionId,
  width = 160,
  height = 120,
  loopMs = 1700,
  pauseMs = 500,
  paused = false
}) => {
  const canvasRef = useRef(null)
  const animRef = useRef(null)
  const startRef = useRef(null)

  useEffect(() => {
    if (paused) {
      if (animRef.current) cancelAnimationFrame(animRef.current)
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const w = canvas.width,
      h = canvas.height
    const render = getRenderer(transitionId)
    startRef.current = null

    const tick = ts => {
      if (!startRef.current) startRef.current = ts
      const totalCycle = loopMs + pauseMs
      const raw = (ts - startRef.current) % totalCycle
      const p = Math.min(raw / loopMs, 1)
      ctx.clearRect(0, 0, w, h)
      render(ctx, w, h, p)
      animRef.current = requestAnimationFrame(tick)
    }
    animRef.current = requestAnimationFrame(tick)

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current)
    }
  }, [transitionId, paused, loopMs, pauseMs])

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="transition-canvas"
    />
  )
}

// ─── GROUP TRANSITIONS BY CATEGORY ────────────────────────────────────────────

const TRANSITION_CATEGORIES = {
  Fade: [
    'fade',
    'fadein',
    'fadeout',
    'fadeblack',
    'fadewhite',
    'dissolve',
    'fadegrays'
  ],
  Wipe: ['wipeleft', 'wiperight', 'wipeup', 'wipedown'],
  Slide: ['slideleft', 'slideright', 'slideup', 'slidedown'],
  Smooth: ['smoothleft', 'smoothright', 'smoothup', 'smoothdown'],
  Circle: ['circlecrop', 'circleclose', 'circleopen'],
  Cover: ['coverleft', 'coverright', 'coverup', 'coverdown'],
  Reveal: ['revealleft', 'revealright', 'revealup', 'revealdown'],
  Split: ['horzclose', 'horzopen', 'vertclose', 'vertopen', 'rectcrop'],
  Diagonal: ['diagtl', 'diagtr', 'diagbl', 'diagbr', 'radial'],
  Slice: ['hlslice', 'hrslice', 'vuslice', 'vdslice'],
  Effects: ['pixelize', 'squeezeh', 'squeezev', 'zoomin', 'hblur']
}

export const TransitionModal = ({
  visible,
  onClose,
  onConfirm,
  currentTransition,
  onApplyToAll,
  onRemoveFromAll
}) => {
  const currentType =
    typeof currentTransition === 'string'
      ? currentTransition
      : currentTransition?.transition || null
  const currentDuration =
    typeof currentTransition === 'object' ? currentTransition?.duration || 1 : 1

  const [selectedType, setSelectedType] = useState(currentType)
  const [duration, setDuration] = useState(currentDuration)
  const [activeCategory, setActiveCategory] = useState('Fade')
  // Track which card is being hovered so we can pause others if needed
  const [hoveredId, setHoveredId] = useState(null)

  useEffect(() => {
    if (visible) {
      const type =
        typeof currentTransition === 'string'
          ? currentTransition
          : currentTransition?.transition || null
      const dur =
        typeof currentTransition === 'object'
          ? currentTransition?.duration || 1
          : 1
      setSelectedType(type)
      setDuration(dur)

      if (type) {
        for (const [category, transitionIds] of Object.entries(
          TRANSITION_CATEGORIES
        )) {
          if (transitionIds.includes(type)) {
            setActiveCategory(category)
            break
          }
        }
      }
    }
  }, [visible, currentTransition])

  const getTransition = id => transitions.find(t => t.id === id)

  const handleConfirm = () => {
    if (selectedType) {
      onConfirm({ transition: selectedType, duration })
    } else {
      onConfirm(null)
    }
    onClose()
  }

  const handleRemove = () => {
    onConfirm(null)
    onClose()
  }

  const handleApplyToAll = () => {
    if (!selectedType || !onApplyToAll) return
    const transitionName = getTransition(selectedType)?.name || selectedType
    Modal.confirm({
      title: 'Apply transition to all?',
      content: `This will apply "${transitionName}" (${duration}s) to all transitions in the project.`,
      centered: true,
      okText: 'Apply to all',
      cancelText: 'Cancel',
      onOk: () => {
        onApplyToAll({ transition: selectedType, duration })
        onClose()
      }
    })
  }

  const handleRemoveFromAll = () => {
    if (!onRemoveFromAll) return
    Modal.confirm({
      title: 'Remove all transitions?',
      content:
        'This will remove transitions from all scenes and shots in the project.',
      centered: true,
      okType: 'danger',
      okText: 'Remove all',
      cancelText: 'Cancel',
      onOk: () => {
        onRemoveFromAll()
        onClose()
      }
    })
  }

  const durationMarks = {
    1: '1s',
    1.5: '',
    2: '2s',
    2.5: '',
    3: '3s'
  }

  const selectedTransition = getTransition(selectedType)

  return (
    <Modal
      title="Select Transition"
      visible={visible}
      onCancel={onClose}
      width={680}
      centered
      destroyOnClose
      className="transition-modal"
      footer={
        <div className="transition-footer-actions">
          {selectedType && (
            <Button className="remove-btn" onClick={handleRemove}>
              <Icon type="delete" /> Remove
            </Button>
          )}
          <Button type="primary" onClick={handleConfirm} disabled={!selectedType}>
            {selectedType ? 'Apply Transition' : 'Apply No Transition'}
          </Button>
        </div>
      }
    >
      <div className="transition-bulk-toolbar">
        <span className="toolbar-label">Bulk actions</span>
        <div className="toolbar-actions">
          <Button
            className="bulk-btn bulk-btn--danger"
            size="small"
            onClick={handleRemoveFromAll}
          >
            Remove from all
          </Button>
          <Button
            className="bulk-btn"
            size="small"
            type="primary"
            ghost
            onClick={handleApplyToAll}
            disabled={!selectedType}
          >
            Apply to all
          </Button>
        </div>
      </div>

      {/* Category pills */}
      <div className="category-bar-container">
        <div className="category-bar">
          {Object.keys(TRANSITION_CATEGORIES).map(category => (
            <div
              key={category}
              className={`category-pill ${activeCategory === category ? 'active' : ''
                }`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </div>
          ))}
        </div>
      </div>

      {/* Transition grid — each card has a live canvas preview */}
      <div className="transition-content">
        <div className="transition-grid">
          {TRANSITION_CATEGORIES[activeCategory].map(id => {
            const transition = getTransition(id)
            if (!transition) return null
            const isSelected = selectedType === id

            return (
              <div
                key={id}
                className={`transition-item ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedType(id)}
                onMouseEnter={() => setHoveredId(id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Canvas preview replacing the static icon */}
                <div className="transition-preview-wrap">
                  <TransitionCanvas
                    transitionId={id}
                    width={120}
                    height={72}
                    loopMs={1700}
                    pauseMs={500}
                  />
                </div>
                <span className="transition-name">{transition.name}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Configuration Panel */}
      {selectedType && selectedTransition ? (
        <div className="modal-settings-area">
          <div className="settings-header">
            {/* Larger animated preview in the settings header */}
            <div className="selected-icon-preview">
              <TransitionCanvas
                transitionId={selectedType}
                width={48}
                height={48}
                loopMs={1700}
                pauseMs={500}
              />
            </div>
            <div className="selected-info">
              <span className="label">SELECTED TRANSITION</span>
              <span className="value">{selectedTransition.name}</span>
              <span className="duration">{duration || 1}s</span>
            </div>
          </div>

          <div className="duration-control-wrapper">
            <span className="duration-label">Duration</span>
            <div className="duration-slider-container">
              <Slider
                min={1}
                max={3}
                step={0.5}
                marks={durationMarks}
                value={duration}
                onChange={setDuration}
                tooltipVisible={false}
              />
            </div>
            <div className="duration-value-badge">{duration}s</div>
          </div>

        </div>
      ) : (
        <div className="modal-settings-area empty">
          Select a transition above to configure settings
        </div>
      )}

    </Modal>
  )
}

const SceneTransition = ({
  sceneId,
  position,
  transitionType,
  handleTransitionSelect,
  handleApplyTransitionToAll,
  handleRemoveAllTransitions,
  compact = false,
  compactLabel = 'Transition'
}) => {
  const [modalVisible, setModalVisible] = useState(false)

  const handleConfirm = transitionData => {
    handleTransitionSelect(sceneId, position, transitionData)
  }

  const normalizedType =
    typeof transitionType === 'string'
      ? { transition: transitionType, duration: 1 }
      : transitionType
  const selectedTransition = transitions.find(
    transition => transition.id === normalizedType?.transition
  )
  const transitionName = selectedTransition?.name || 'None'
  const transitionDuration = normalizedType?.duration || 1

  if (compact) {
    return (
      <>
        <div
          className={`transition-chip transition-chip--${position}`}
          onClick={event => {
            event.stopPropagation()
            setModalVisible(true)
          }}
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setModalVisible(true)
            }
          }}
        >
          <Icon type="swap" />
          <span>{compactLabel}</span>
          <span className="transition-chip__value">
            {transitionName}
            {selectedTransition ? ` ${transitionDuration}s` : ''}
          </span>
        </div>

        <TransitionModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onConfirm={handleConfirm}
          currentTransition={transitionType}
          onApplyToAll={handleApplyTransitionToAll}
          onRemoveFromAll={handleRemoveAllTransitions}
        />
      </>
    )
  }

  return (
    <div className={`transition-wrapper ${position}-transition`}>
      <div className="transition-trigger" onClick={() => setModalVisible(true)}>
        <TransitionDisplay
          type={transitionType}
          onRemove={() => {
            handleTransitionSelect(sceneId, position, null)
          }}
        />
      </div>

      <TransitionModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onConfirm={handleConfirm}
        currentTransition={transitionType}
        onApplyToAll={handleApplyTransitionToAll}
        onRemoveFromAll={handleRemoveAllTransitions}
      />
    </div>
  )
}

export default SceneTransition
